import { forwardRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Loader2 } from "lucide-react";
import { springs } from "../motion";
import type { ButtonProps } from "./types";

const variantClasses: Record<string, string> = {
  primary: "bg-accent text-white hover:bg-accent/90",
  secondary:
    "bg-white border border-border text-secondary hover:text-primary hover:bg-card-hover",
  ghost: "bg-transparent text-secondary hover:text-primary hover:bg-card-hover",
  danger: "bg-red-600 text-white hover:bg-red-700",
  "accent-soft": "bg-accent/10 text-accent hover:bg-accent/20",
};

const sizeClasses: Record<string, string> = {
  sm: "h-7 px-2 text-xs gap-1.5",
  md: "h-[34px] px-3 text-[14px] gap-1.5",
};

const iconOnlySizeClasses: Record<string, string> = {
  sm: "h-7 w-7",
  md: "h-[34px] w-[34px]",
};

const iconSizeClasses: Record<string, string> = {
  sm: "h-3.5 w-3.5",
  md: "h-3.5 w-3.5",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      variant = "secondary",
      size = "md",
      icon: Icon,
      iconOnly = false,
      loading = false,
      success = false,
      disabled,
      className = "",
      children,
      ...rest
    },
    ref,
  ) {
    const base =
      "inline-flex items-center justify-center font-medium rounded-button transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed";
    const vClass = variantClasses[variant];
    const sClass = iconOnly ? iconOnlySizeClasses[size] : sizeClasses[size];

    return (
      <motion.button
        ref={ref}
        type="button"
        whileTap={{ scale: 0.95 }}
        transition={springs.snappy}
        disabled={disabled || loading}
        className={`${base} ${vClass} ${sClass} ${className}`}
        {...rest}
      >
        <AnimatePresence mode="wait" initial={false}>
          {loading ? (
            <motion.span
              key="loading"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={springs.snappy}
              className="flex items-center gap-2"
            >
              <Loader2 className={`${iconSizeClasses[size]} animate-spin`} />
              {!iconOnly && children}
            </motion.span>
          ) : success ? (
            <motion.span
              key="success"
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.6 }}
              transition={springs.bouncy}
              className="flex items-center justify-center"
            >
              <Check className={iconSizeClasses[size]} />
            </motion.span>
          ) : (
            <motion.span
              key="content"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.1 }}
              className="flex items-center gap-1.5"
            >
              {Icon && <Icon className={iconSizeClasses[size]} />}
              {!iconOnly && children}
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>
    );
  },
);
